import React, { useContext, useState } from "react";

import classNames from "classnames";
import styled from "styled-components";

import ComponentContext from "../../../contexts/ComponentContext";
import breakpoints from "../../constants";
import Markdown from "../../styles/Markdown";

const StyledHoverImageBoxes = styled.div`
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${({ $backgroundColor }) =>
    $backgroundColor || "transparent"};
  ${(props) => props.$dynamicStyles}
`;

const StyledImageContainer = styled.div`
  position: relative;
  width: 100%;
  min-height: ${({ height, mobileHeight }) => mobileHeight || height || "70vh"};
  overflow: hidden;

  @media (min-width: ${breakpoints.TABLET}) {
    min-height: ${({ height }) => height || "80vh"};
  }
`;

const StyledDefaultBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: ${({ $backgroundSize }) => $backgroundSize || "cover"};
  background-position: ${({ $backgroundPosition }) =>
    $backgroundPosition || "center"};
  background-repeat: no-repeat;
  opacity: ${({ $isHovered }) => ($isHovered ? 0 : 1)};
  transition: opacity 0s; /* No transition for instant swap */
`;

const StyledHoverBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-size: ${({ $backgroundSize }) => $backgroundSize || "cover"};
  background-position: ${({ $backgroundPosition }) =>
    $backgroundPosition || "center"};
  background-repeat: no-repeat;
  opacity: ${({ $isHovered }) => ($isHovered ? 1 : 0)};
  transition: opacity 0s; /* No transition for instant swap */
`;

const StyledBoxesContainer = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  z-index: 10;
`;

const StyledBox = styled.div`
  position: relative;
  width: 100%;
  cursor: ${({ onClick }) => (onClick ? "pointer" : "default")};
`;

function HoverImageBoxes() {
  const {
    title,
    defaultImage,
    boxes = [],
    height = "80vh",
    mobileHeight,
    backgroundSize = "cover",
    backgroundPosition = "center",
    boxesClassName,
    CSS,
    wrapperClassName,
    fullWidth = false,
    backgroundColor,
  } = useContext(ComponentContext);

  const [hoveredIndex, setHoveredIndex] = useState(null);

  const rowClassName = classNames({ row: !fullWidth }, wrapperClassName);

  // Normalize boxes array - handle both v5 format and direct array/component array
  const boxesArray =
    Array.isArray(boxes) && boxes.length > 0 ? boxes : boxes?.data || [];

  // Get default image URL
  const defaultImageUrl =
    defaultImage?.url ||
    (Array.isArray(defaultImage) && defaultImage[0]?.url) ||
    "";

  if (boxesArray.length === 0) {
    return null;
  }

  const handleBoxClick = (box, index) => {
    if (box?.url) {
      window.location.href = box.url;
    } else if (box?.onClick) {
      box.onClick(box, index);
    }
  };

  // Get the currently hovered box's image
  const hoveredBox = hoveredIndex !== null ? boxesArray[hoveredIndex] : null;
  const hoveredImageUrl =
    hoveredBox?.image?.url ||
    (Array.isArray(hoveredBox?.image) && hoveredBox.image[0]?.url) ||
    "";

  return (
    <StyledHoverImageBoxes
      className={rowClassName}
      $dynamicStyles={CSS}
      $backgroundColor={backgroundColor}
    >
      {title && (
        <div className="container-xxl" style={{ marginBottom: "2rem" }}>
          <Markdown>{title}</Markdown>
        </div>
      )}

      <div className={`${fullWidth ? "" : "container-xxl"} box-and-image-container`}>
        <StyledImageContainer height={height} mobileHeight={mobileHeight}>
          {/* Default Background Image */}
          {defaultImageUrl && (
            <StyledDefaultBackground
              style={{
                backgroundImage: `url(${defaultImageUrl})`,
              }}
              $backgroundSize={backgroundSize}
              $backgroundPosition={backgroundPosition}
              $isHovered={hoveredIndex !== null && hoveredImageUrl}
              className="default-background-image"
            />
          )}

          {/* Hover Background Image */}
          {hoveredImageUrl && (
            <StyledHoverBackground
              style={{
                backgroundImage: `url(${hoveredImageUrl})`,
              }}
              $backgroundSize={
                hoveredBox?.backgroundSize ||
                hoveredBox?.imageBackgroundSize ||
                backgroundSize
              }
              $backgroundPosition={
                hoveredBox?.backgroundPosition ||
                hoveredBox?.imageBackgroundPosition ||
                backgroundPosition
              }
              $isHovered={hoveredIndex !== null}
            />
          )}

          {/* Boxes Overlay */}
          <StyledBoxesContainer className={boxesClassName}>
            {boxesArray.map((box, index) => {
              return (
                <StyledBox
                  key={box?.id || box?.title || index}
                  onMouseEnter={() => setHoveredIndex(index)}
                  onMouseLeave={() => setHoveredIndex(null)}
                  onClick={() => handleBoxClick(box, index)}
                >
                  {box?.content && (
                    <Markdown>{box.content}</Markdown>
                  )}
                </StyledBox>
              );
            })}
          </StyledBoxesContainer>
        </StyledImageContainer>
      </div>
    </StyledHoverImageBoxes>
  );
}

export default HoverImageBoxes;
